import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Form, InputNumber } from 'antd';
import { Button, Drawer, Toggle } from '@prio365/prio365-react-library';
import { makePrioStyles } from '../../../../theme/utils';
import { EmployeeId, OfficeId } from '../../../../models/Types';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../../theme/types';
import {
  CompensationPayment,
  CompensationPaymentsCalculatedData,
  CreateCompensationPayment,
  MonthlyCloseSearchResultItem,
} from '../../../../models/TimeKeeping';
import { useForm } from 'antd/lib/form/Form';
import moment, { Moment } from 'moment';
import Flex from '../../../../components/Flex';
import CustomSingleDatePicker from '../../../../components/CustomSingleDatePicker';
import { apiCreateCompensationPayment } from '../../../timeKeeping/api';
import useContactsContext from '../../../contacts/hooks/useContactsProvider';
import useFilterContext from '../../../../components/Filter/hooks/useFilterContext';
import { createTemporaryId } from '../../../../util';
import { useSelector } from 'react-redux';
import { getUserMe } from '../../../../apps/main/rootReducer';
import ContactPickerPrioCL from '../../../contacts/components/ContactPickerPrioCL';

const useStyles = makePrioStyles((theme) => ({
  root: {
    '& .prio-drawer-content': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    '& .input-label-and-field': {
      gap: 0,
    },
    '& .ant-form-item': {
      marginBottom: theme.spacing.regular,
      marginTop: theme.spacing.regular,
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    '& .ant-select-selection-item $overTime': {
      display: 'none',
    },
  },
  firstRow: {
    '& .ant-form-item': {
      marginTop: 0,
    },
  },
  companyText: {
    fontSize: 14,
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  toggleFormItem: {
    '& .ant-form-item-control-input': {
      height: '40px',
    },
    '& input': {
      display: 'none',
    },
  },
  inputNumber: {
    width: '100%',
    '& .ant-input-number-input-wrap': {
      height: '38px',
      alignContent: 'center',
    },
  },
}));

interface EmployeeData {
  employeeId: EmployeeId;
  hours: number;
  hasBeenPaidOut: boolean;
}

interface CreateMultipleCompensationPayments {
  expectedPayoutDate: Moment;
  employeeData: EmployeeData[];
}

const defaultEmployeeData: EmployeeData = {
  employeeId: undefined,
  hours: 0,
  hasBeenPaidOut: false,
};

interface CreateCompensationPaymentsDrawerProps {
  className?: string;
  visible: boolean;
  officeId: OfficeId;
  monthlyCloses?: MonthlyCloseSearchResultItem[];
  onClose: () => void;
  isMonthlyClosePage?: boolean;
}

export const CreateCompensationPaymentsDrawer: React.FC<
  CreateCompensationPaymentsDrawerProps
> = (props) => {
  //#region ------------------------------ Defaults
  const {
    className,
    visible,
    monthlyCloses: _monthlyCloses,
    officeId,
    onClose,
    isMonthlyClosePage = false,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  const [form] = useForm<CreateMultipleCompensationPayments>();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { optimisticWrite } = useFilterContext<
    CompensationPayment,
    CompensationPaymentsCalculatedData
  >();

  const userMe = useSelector(getUserMe);

  const { getContactById } = useContactsContext();

  const monthlyCloses = useMemo(() => _monthlyCloses || [], [_monthlyCloses]);

  const initialValue: CreateMultipleCompensationPayments = useMemo(
    () => ({
      expectedPayoutDate: moment().endOf('month'),
      employeeData:
        monthlyCloses.length > 0
          ? [
              ...monthlyCloses.map(
                ({
                  data: { employeeId },
                  calculated: { unsettledOvertimeHours },
                }) => {
                  return {
                    employeeId,
                    hours: unsettledOvertimeHours,
                    hasBeenPaidOut: false,
                  };
                }
              ),
              defaultEmployeeData,
            ]
          : [defaultEmployeeData],
    }),
    [monthlyCloses]
  );

  const [formDisabled, setFormDisabled] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean | undefined>(undefined);
  const [actionDisabled, setActionDisabled] = useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleOnClose = () => {
    form.resetFields();
    form.setFieldsValue(initialValue);
    setFormDisabled(initialValue.employeeData.length <= 1);
    onClose();
  };

  const handleFinish = async () => {
    form.validateFields().then(async () => {
      const value = form.getFieldsValue();
      if (!isMonthlyClosePage) {
        optimisticWrite?.(
          value.employeeData
            .filter(({ employeeId }) => employeeId)
            .map((employeeData) => {
              const hasBeenPaidOut = employeeData.hasBeenPaidOut;
              const createCompensationPayment: CreateCompensationPayment = {
                employeeId: employeeData.employeeId,
                ...(!employeeData.hasBeenPaidOut
                  ? {
                      expectedPayoutDate: value.expectedPayoutDate
                        .toISOString(true)
                        .split('T')[0],
                    }
                  : {}),
                hours: employeeData.hours,
                hasBeenPaidOut: employeeData.hasBeenPaidOut,
              };
              const compensationPayment: CompensationPayment = {
                compensationPaymentId: createTemporaryId(),
                createdDate: moment().toISOString(true),
                createdBy: userMe?.id,
                expectedPayoutDate:
                  createCompensationPayment.expectedPayoutDate,
                payoutDate: hasBeenPaidOut ? moment().toISOString(true) : null,
                paidBy: hasBeenPaidOut ? userMe?.id : null,
                hours: createCompensationPayment.hours,
                monthlyCloseId: createTemporaryId(),
                employeeId: createCompensationPayment.employeeId,
              };
              return {
                calculated: {
                  officeId,
                  isApproved: hasBeenPaidOut,
                  createdById: userMe?.id,
                  paidById: hasBeenPaidOut ? userMe?.id : null,
                  employeeIsArchived: getContactById(employeeData.employeeId)
                    ?.isArchived,
                },
                data: compensationPayment,
                method: 'add',
                callback: async () => {
                  const { result, data } = await apiCreateCompensationPayment(
                    createCompensationPayment,
                    getContactById(employeeData.employeeId)?.officeId
                  );
                  return {
                    result,
                    data: {
                      data,
                      calculated: {
                        officeId,
                        isApproved: hasBeenPaidOut,
                        createdById: userMe?.id,
                        paidById: hasBeenPaidOut ? userMe?.id : null,
                        employeeIsArchived: getContactById(
                          employeeData.employeeId
                        )?.isArchived,
                      },
                      isTemporary: false,
                    },
                  };
                },
              };
            })
        );
      } else {
        setIsSaving(true);
        const promises = value.employeeData
          .filter(({ employeeId }) => !!employeeId)
          .map(async (employeeData) => {
            const hasBeenPaidOut = employeeData.hasBeenPaidOut;
            const createCompensationPayment: CreateCompensationPayment = {
              employeeId: employeeData.employeeId,
              ...(!employeeData.hasBeenPaidOut
                ? {
                    expectedPayoutDate: value.expectedPayoutDate
                      .toISOString(true)
                      .split('T')[0],
                  }
                : {}),
              hours: employeeData.hours,
              hasBeenPaidOut: hasBeenPaidOut,
            };
            return await apiCreateCompensationPayment(
              createCompensationPayment,
              getContactById(employeeData.employeeId)?.officeId
            );
          });
        await Promise.all(promises);
      }

      handleOnClose();
      setIsSaving(false);
    });
  };

  const handleOnCancel = () => {
    handleOnClose();
  };

  const handleOnValuesChange = (
    changedValues: any,
    allValues: CreateMultipleCompensationPayments
  ) => {
    const validEmployeeData =
      allValues.employeeData.filter(({ employeeId }) => employeeId) ?? [];

    const hoursZero = validEmployeeData.filter(({ hours }) => hours === 0)
      .length as number;

    setActionDisabled(hoursZero > 0);

    if (changedValues.employeeData) {
      let data = allValues.employeeData;

      if (validEmployeeData.length > 1) {
        data = data.filter(
          ({ employeeId }, index) =>
            employeeId || index === allValues.employeeData.length - 1
        );
        form.setFieldsValue({
          employeeData: data,
        });
      }
      setFormDisabled(data.length <= 1);
    }
  };

  const handleOnSelect: (value: EmployeeId) => void = (value) => {
    let employeeData: EmployeeData[] = form.getFieldValue('employeeData');
    const index = employeeData.findIndex((entry) => entry.employeeId === value);
    if (index > -1) {
      const { employeeId } = employeeData[index];
      const monthlyClose = monthlyCloses.find(
        ({ data: { employeeId: eId } }) => employeeId === eId
      );
      if (monthlyClose) {
        employeeData[index].hours =
          monthlyClose.calculated.unsettledOvertimeHours;
        form.setFieldsValue({
          employeeData,
        });
      }
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(initialValue);
    setFormDisabled(initialValue.employeeData.length <= 1);
  }, [form, initialValue]);
  //#endregion

  return (
    <Drawer
      className={classNames(classes.root, className)}
      visible={visible}
      onClose={() => handleOnClose()}
      closable
      title={t(
        'hr:timeAndLeaveManagement.createCompensationPaymentsDrawer.title'
      )}
    >
      <Form<CreateMultipleCompensationPayments>
        className={classes.form}
        form={form}
        initialValues={initialValue}
        layout="vertical"
        onValuesChange={handleOnValuesChange}
      >
        <Flex.Column flex={1} overflowY="auto" overflowX="hidden">
          <Flex.Row className={classes.firstRow}>
            <Form.Item
              name="expectedPayoutDate"
              label={t(
                'hr:timeAndLeaveManagement.createCompensationPaymentsDrawer.expectedPayoutDate'
              )}
              style={{ flex: 1 }}
              rules={[
                {
                  required: true,
                  message: t(
                    'hr:timeAndLeaveManagement.monthlyCloseForm.warning.expectedPayoutDate'
                  ),
                },
              ]}
            >
              <CustomSingleDatePicker
                id="create_compensation_payments_id"
                anchorDirection={'ANCHOR_RIGHT'}
                small={true}
                regular={false}
                twoMonths={false}
                withFullScreenPortal={false}
                daySize={30}
                hideKeyboardShortcutsPanel={true}
                showDefaultInputIcon={true}
                inputIconPosition={'after'}
              />
            </Form.Item>
          </Flex.Row>
          <Form.List name="employeeData">
            {(fields, { add }) => {
              return fields.map((field, index) => {
                return (
                  <Flex.Row
                    childrenGap={theme.spacing.regular}
                    alignItems={'center'}
                  >
                    <Flex.Column flex={index === fields.length - 1 ? 1 : 2}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'employeeId']}
                        label={
                          index === 0 &&
                          t(
                            'hr:timeAndLeaveManagement.createCompensationPaymentsDrawer.employeeId'
                          )
                        }
                        rules={
                          index < fields.length - 1 && [
                            {
                              required: true,
                            },
                          ]
                        }
                      >
                        <ContactPickerPrioCL
                          disabled={index < fields.length - 1 && formDisabled}
                          officeId={officeId}
                          contactType="InternalContact"
                          label={
                            index === fields.length - 1 &&
                            `${t('common:add')}...`
                          }
                          onChange={(value) => {
                            if (index === fields.length - 1) {
                              add(defaultEmployeeData);
                            }
                            handleOnSelect(value as string);
                          }}
                          allowClear
                          size="default"
                          hideShowMoreButton
                        />
                      </Form.Item>
                    </Flex.Column>
                    {index < fields.length - 1 && (
                      <Flex.Column flex={1}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'hours']}
                          label={
                            index === 0 &&
                            t(
                              'hr:timeAndLeaveManagement.createCompensationPaymentsDrawer.hours'
                            )
                          }
                          rules={[
                            {
                              required: true,
                              message: t(
                                'hr:timeAndLeaveManagement.createCompensationPaymentsDrawer.warning.missingHours'
                              ),
                            },
                            {
                              validator(_, value) {
                                if (value > 0) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(
                                  t(
                                    'hr:timeAndLeaveManagement.createCompensationPaymentsDrawer.warning.hoursGreaterZero'
                                  )
                                );
                              },
                            },
                          ]}
                        >
                          <InputNumber
                            min={0.25}
                            step={0.25}
                            precision={2}
                            formatter={(value) => `${value}`.replace('.', ',')}
                            parser={(value) =>
                              parseFloat(value.replace(',', '.')) as 0.25
                            }
                            disabled={formDisabled}
                            className={classes.inputNumber}
                          />
                        </Form.Item>
                      </Flex.Column>
                    )}
                    {index < fields.length - 1 && (
                      <Flex.Column flex={1}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'hasBeenPaidOut']}
                          label={
                            index === 0 &&
                            t(
                              'hr:timeAndLeaveManagement.createCompensationPaymentsDrawer.hasBeenPaidOut'
                            )
                          }
                          valuePropName="checked"
                          className={classes.toggleFormItem}
                        >
                          <Toggle disabled={formDisabled} />
                        </Form.Item>
                      </Flex.Column>
                    )}
                    {index < fields.length - 1 && (
                      <Button
                        iconProp={['fal', 'xmark']}
                        type="link"
                        tooltip={t('common:actions.remove')}
                        onClick={() => {
                          const employeeData =
                            form.getFieldValue('employeeData');
                          employeeData.splice(index, 1);
                          form.setFieldsValue({
                            employeeData,
                          });
                          setFormDisabled(employeeData.length <= 1);
                        }}
                        style={{
                          marginTop: index === 0 ? '28px' : null,
                        }}
                      />
                    )}
                  </Flex.Row>
                );
              });
            }}
          </Form.List>
        </Flex.Column>
        <Flex.Row justifyContent="flex-end">
          <Button
            onClick={handleOnCancel}
            type="default"
            style={{ marginRight: '8px' }}
            disabled={isSaving}
          >
            {t(
              'hr:timeAndLeaveManagement.createCompensationPaymentsDrawer.cancel'
            )}
          </Button>
          <Button
            onClick={handleFinish}
            disabled={actionDisabled || formDisabled || isSaving}
            loading={isSaving}
          >
            {t(
              'hr:timeAndLeaveManagement.createCompensationPaymentsDrawer.payOut'
            )}
          </Button>
        </Flex.Row>
      </Form>
    </Drawer>
  );
};

export default CreateCompensationPaymentsDrawer;
